import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { lazy, Suspense, type ReactNode } from "react";
import { ThemeProvider } from "next-themes";
import { useSession } from "@/lib/auth-client";
import { Toaster } from "@/components/ui/sonner";
import { AppShell } from "@/components/layout/app-shell";
import interCss from "@fontsource-variable/inter/index.css?url";
import globalsCss from "@/styles/globals.css?url";

interface RouterContext {
  queryClient: QueryClient;
}

if (import.meta.env.DEV && typeof window !== "undefined") {
  import("react-grab");
}

const RouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        name: "description",
        content: "BibaCV. Camera analytics for retail stores.",
      },
      { title: "BibaCV" },
    ],
    links: [
      { rel: "stylesheet", href: interCss },
      { rel: "stylesheet", href: globalsCss },
    ],
  }),
  component: RootLayout,
});

function RootLayout() {
  const { data: session, isPending } = useSession();
  // Docs bring their own full-page layout (fumadocs), so the app shell steps aside.
  const isDocs = useRouterState({ select: (s) => s.location.pathname.startsWith("/docs") });

  if (isDocs) {
    return (
      <RootDocument>
        <Outlet />
      </RootDocument>
    );
  }

  return (
    <RootDocument>
      {isPending ? (
        <div className="min-h-svh bg-background" />
      ) : session?.user ? (
        <AppShell>
          <Outlet />
        </AppShell>
      ) : (
        <div className="min-h-svh bg-background">
          <Outlet />
        </div>
      )}
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          {children}
          <Toaster />
        </ThemeProvider>
        <Suspense>
          <RouterDevtools />
        </Suspense>
        <Scripts />
      </body>
    </html>
  );
}
